
import {
  computed, ComputedRef, defineComponent, PropType, ref, Ref, watch,
} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { StringMap } from '@/types';

interface CombinationItem {
  id_shop_product_attribute: number;
  id_shop_product: number;
  id_combination: number;
  combination_name: string;
  reference: string;
  price: number;
  price_discount: number;
  quantity: number;
  image_link?: string;
}

export default defineComponent({
  name: 'ChooseVariantsModal',
  components: {
    Dialog,
    InputText,
    Button,
  },
  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },
    product: {
      type: Object as PropType<StringMap | null>,
      required: false,
      default: null,
    },
    variants: {
      type: Array as PropType<CombinationItem[]>,
      required: false,
      default: () => [],
    },
    fields: {
      type: Object as PropType<StringMap>,
      required: true,
    },
    initialProductSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialSelectedVariantIds: {
      type: Array as PropType<CombinationItem[]>,
      required: false,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoadingMore: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasMoreResults: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: {
    'on-close-dialog': null,
    'on-validate-selection': Object,
    'on-search-change': String,
    'on-load-more': null,
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const displayModal: ComputedRef<boolean> = computed(() => props.displayDialog);
    const searchValue: Ref<string> = ref('');
    const localSelectProduct = ref(false);
    const selectedVariantMap: Ref<Record<string, CombinationItem>> = ref({});
    const availableListRef = ref<HTMLElement | null>(null);

    const resetLocalState = () => {
      searchValue.value = '';
      localSelectProduct.value = props.initialProductSelected;
      selectedVariantMap.value = {};
      if (props.initialProductSelected) {
        return;
      }
      props.initialSelectedVariantIds.forEach((variant) => {
        selectedVariantMap.value[String(variant.id_shop_product_attribute)] = variant;
      });
    };

    watch(
      () => props.displayDialog,
      (visible) => {
        if (visible) {
          resetLocalState();
        }
      },
      { immediate: true },
    );

    const selectedVariants = computed(() => Object.values(selectedVariantMap.value));

    const selectedCount = computed(
      () => (localSelectProduct.value ? 1 : selectedVariants.value.length),
    );

    const isVariantSelected = (variantId: number) => Boolean(selectedVariantMap.value[String(variantId)]);

    const toggleSelectProduct = () => {
      localSelectProduct.value = !localSelectProduct.value;
      if (localSelectProduct.value) {
        selectedVariantMap.value = {};
      }
    };

    const toggleVariant = (variant: CombinationItem) => {
      const key = String(variant.id_shop_product_attribute);
      if (selectedVariantMap.value[key]) {
        delete selectedVariantMap.value[key];
        return;
      }
      if (localSelectProduct.value) {
        localSelectProduct.value = false;
      }
      selectedVariantMap.value[key] = variant;
    };

    const getVariantLabel = (variant: CombinationItem) => variant.combination_name
      || variant.reference
      || `#${variant.id_combination}`;

    const getProductImage = (): string => String(
      props.product?.image_link
      ?? props.product?.image_url
      ?? '',
    ).trim();

    const getVariantImage = (variant: CombinationItem): string => String(
      variant.image_link
      ?? '',
    ).trim();

    const handleSearchInput = () => {
      emit('on-search-change', searchValue.value);
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        emit('on-load-more');
      }
    };

    const handleClose = () => {
      emit('on-close-dialog');
    };

    const handleValidate = () => {
      emit('on-validate-selection', {
        selectProduct: localSelectProduct.value,
        variants: selectedVariants.value,
      });
    };

    return {
      t,
      displayModal,
      searchValue,
      localSelectProduct,
      availableListRef,
      selectedVariants,
      selectedCount,
      isVariantSelected,
      toggleSelectProduct,
      toggleVariant,
      getVariantLabel,
      getProductImage,
      getVariantImage,
      handleSearchInput,
      handleAvailableScroll,
      handleClose,
      handleValidate,
    };
  },
});
